import { useRef, useEffect, useState, useCallback } from 'react';

// Product images with black backgrounds
const COOKWARE_SET_URL = "https://customer-assets.emergentagent.com/job_precision-cookware/artifacts/26s2nma4_Cookware%20Set.png";
const COOKING_POT_URL = "https://customer-assets.emergentagent.com/job_precision-cookware/artifacts/t6sv52gj_Cooking%20pot.png";
const WOK_URL = "https://customer-assets.emergentagent.com/job_precision-cookware/artifacts/6rnlqwlu_Stainless%20Steel%20Wok.png";

// Full cookware collection image (spotlight)
const COOKWARE_COLLECTION_URL = "https://customer-assets.emergentagent.com/job_9f772d4a-b644-48b8-b3b1-90c525e985c8/artifacts/lir2crsf_TexStyle%20Cookware%20Set.png";

// Cooking image for left side - stainless steel pan
const COOKING_IMAGE = "https://images.pexels.com/photos/4252153/pexels-photo-4252153.jpeg?auto=compress&cs=tinysrgb&w=800";

// Lerp utility for smooth interpolation
const lerp = (start, end, factor) => start + (end - start) * factor;

// Steam Animation Component
const SteamAnimation = ({ className = "", delay = 0 }) => (
  <div className={`absolute pointer-events-none ${className}`}>
    {[...Array(5)].map((_, i) => (
      <div
        key={i}
        className="steam-particle absolute"
        style={{
          left: `${20 + i * 15}%`,
          animationDelay: `${delay + i * 0.3}s`,
        }}
      />
    ))}
  </div>
);

// WOW MOMENT 4: Architectural Line Overlay SVG
const ArchitecturalOverlay = ({ mouseOffset, scrollProgress }) => (
  <svg 
    className="absolute inset-0 w-full h-full pointer-events-none"
    viewBox="0 0 800 600"
    preserveAspectRatio="xMidYMid slice"
    style={{
      opacity: Math.max(0, 0.06 - scrollProgress * 0.1),
      transform: `translate3d(${mouseOffset.x * 0.3}px, ${mouseOffset.y * 0.3}px, 0)`
    }}
  >
    <g stroke="white" strokeWidth="0.5" fill="none" opacity="1">
      <path d="M250 350 L250 280 Q250 250 280 250 L520 250 Q550 250 550 280 L550 350" />
      <path d="M260 345 L260 285 Q260 260 285 260 L515 260 Q540 260 540 285 L540 345" strokeDasharray="4 2" />
      <path d="M270 340 L270 290 Q270 270 290 270 L510 270 Q530 270 530 290 L530 340" strokeDasharray="2 4" />
      <line x1="250" y1="350" x2="550" y2="350" />
      <line x1="260" y1="360" x2="540" y2="360" strokeDasharray="8 4" />
      <path d="M250 290 L220 290 Q200 290 200 310 L200 330 Q200 350 220 350 L250 350" />
      <circle cx="210" cy="320" r="3" />
      <path d="M550 290 L580 290 Q600 290 600 310 L600 330 Q600 350 580 350 L550 350" />
      <circle cx="590" cy="320" r="3" />
      <path d="M260 250 Q260 230 280 220 L520 220 Q540 230 540 250" />
      <path d="M270 248 Q270 235 285 228 L515 228 Q530 235 530 248" strokeDasharray="4 2" />
      <ellipse cx="400" cy="200" rx="30" ry="10" />
      <line x1="400" y1="210" x2="400" y2="220" />
      <g strokeDasharray="2 2" opacity="0.5">
        <line x1="200" y1="380" x2="600" y2="380" />
        <line x1="200" y1="375" x2="200" y2="385" />
        <line x1="600" y1="375" x2="600" y2="385" />
        <line x1="180" y1="200" x2="180" y2="360" />
        <line x1="175" y1="200" x2="185" y2="200" />
        <line x1="175" y1="360" x2="185" y2="360" />
      </g>
      <text x="560" y="270" fontSize="8" fill="white" opacity="0.4" fontFamily="DM Mono">SS316</text>
      <text x="560" y="290" fontSize="8" fill="white" opacity="0.4" fontFamily="DM Mono">AL CORE</text>
      <text x="560" y="310" fontSize="8" fill="white" opacity="0.4" fontFamily="DM Mono">SS403</text>
    </g>
  </svg>
);

// WOW MOMENT 5: Magnetic CTA Button
const MagneticButton = ({ children, href, scrollProgress }) => {
  const buttonRef = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const targetOffset = useRef({ x: 0, y: 0 });
  const animationRef = useRef(null);

  useEffect(() => {
    const animate = () => {
      setOffset(prev => ({
        x: lerp(prev.x, targetOffset.current.x, 0.15),
        y: lerp(prev.y, targetOffset.current.y, 0.15)
      }));
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!buttonRef.current || window.innerWidth < 768) return;
    
    const rect = buttonRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const distX = e.clientX - centerX;
    const distY = e.clientY - centerY;
    const distance = Math.sqrt(distX * distX + distY * distY);
    
    if (distance < 120) {
      const strength = (120 - distance) / 120;
      targetOffset.current = {
        x: distX * strength * 0.3,
        y: distY * strength * 0.3
      };
    } else {
      targetOffset.current = { x: 0, y: 0 };
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    targetOffset.current = { x: 0, y: 0 };
    setIsHovered(false);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  return (
    <a
      ref={buttonRef}
      href={href}
      data-testid="hero-cta-primary"
      className={`
        relative inline-block overflow-hidden
        font-raleway text-[11px] font-semibold uppercase tracking-[0.3em]
        text-white px-10 py-4
        transition-shadow duration-300
        ${isHovered ? 'shadow-[0_0_40px_rgba(107,68,204,0.5)]' : ''}
      `}
      style={{
        transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
        opacity: Math.max(0, 1 - scrollProgress * 2)
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
    >
      <span 
        className="absolute inset-0 bg-tex-violet transition-opacity duration-300"
        style={{ opacity: isHovered ? 0 : 1 }}
      />
      <span 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(90deg, #6B44CC 0%, #8B5CF6 50%, #6B44CC 100%)',
          backgroundSize: '200% 100%',
          animation: isHovered ? 'gradientSweep 0.6s ease forwards' : 'none',
          opacity: isHovered ? 1 : 0
        }}
      />
      <span className="relative z-10">{children}</span>
    </a>
  );
};

export const HeroSection = () => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mouseOffset, setMouseOffset] = useState({ x: 0, y: 0 });
  const targetMouseOffset = useRef({ x: 0, y: 0 });
  const animationRef = useRef(null);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      
      const rect = sectionRef.current.getBoundingClientRect();
      const sectionHeight = sectionRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;
      
      const scrolled = -rect.top;
      const maxScroll = sectionHeight - viewportHeight;
      const progress = Math.min(1, Math.max(0, scrolled / maxScroll));
      
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mouse tracking with lerp animation loop
  useEffect(() => {
    const animate = () => {
      setMouseOffset(prev => ({
        x: lerp(prev.x, targetMouseOffset.current.x, 0.08),
        y: lerp(prev.y, targetMouseOffset.current.y, 0.08)
      }));
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, []);

  // Mouse move handler
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!sectionRef.current || window.innerWidth < 768) return;
      
      const rect = sectionRef.current.getBoundingClientRect();
      if (e.clientY > rect.bottom || e.clientY < rect.top) {
        targetMouseOffset.current = { x: 0, y: 0 };
        return;
      }
      
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      
      const normalizedX = (e.clientX - centerX) / centerX;
      const normalizedY = (e.clientY - centerY) / centerY;
      
      targetMouseOffset.current = {
        x: normalizedX * 20,
        y: normalizedY * 20
      };
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Parallax speeds
  const bgSpeed = 0.2;
  const productSpeed = 0.5;

  const bgTransform = scrollProgress * 100 * bgSpeed;
  const productTransform = scrollProgress * 100 * productSpeed;
  const textOpacity = Math.max(0, 1 - scrollProgress * 1.5);

  // Scene transition phases
  const phase1End = 0.4;
  const kitchenOpacity = Math.min(1, scrollProgress * 2.5);
  const potsSettled = Math.min(1, scrollProgress / phase1End);

  return (
    <section 
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-[300vh] bg-black"
    >
      {/* Sticky Container */}
      <div className="sticky top-0 h-screen overflow-hidden">
        
        {/* LAYER 1 — Background kitchen environment */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            transform: `translate3d(${-mouseOffset.x * 0.4}px, ${-mouseOffset.y * 0.4 + (100 - bgTransform * 2)}%, 0)`,
            opacity: kitchenOpacity
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black via-[#0a0a0a] to-transparent" />
          <div 
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px]"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(255, 200, 150, 0.06) 0%, transparent 70%)',
              filter: 'blur(40px)'
            }}
          />
          <div 
            className="absolute bottom-0 left-0 right-0 h-[40%]"
            style={{
              background: 'linear-gradient(to top, rgba(20, 18, 22, 0.95) 0%, rgba(15, 13, 18, 0.7) 50%, transparent 100%)'
            }}
          />
          <div 
            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-[60%]"
            style={{
              background: 'radial-gradient(ellipse 80% 50% at 50% 100%, rgba(107, 68, 204, 0.12) 0%, transparent 60%)'
            }}
          />
        </div>

        {/* LEFT SIDE - Typography and content */}
        <div 
          className="absolute inset-0 z-30 flex items-center pointer-events-none"
          style={{ 
            opacity: textOpacity,
            transform: `translate3d(${mouseOffset.x * 0.2}px, ${mouseOffset.y * 0.2}px, 0)`
          }}
        >
          <div className="w-full px-6 md:px-12 lg:px-16 xl:px-24 lg:w-[55%]">
            <h1 
              data-testid="hero-heading"
              className="text-editorial-hero text-white relative drop-shadow-lg"
              style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}
            >
              <span className="block">Where precision</span>
              <span className="block text-tex-violet-bright">meets</span>
              <span className="block">the pleasure</span>
              <span className="block">of cooking.</span>
            </h1>
          </div>
        </div>

        {/* RIGHT SIDE - Cooking Image with parallax */}
        <div 
          className="absolute right-0 top-0 bottom-0 w-full md:w-1/2 lg:w-[45%] z-20 overflow-hidden"
          style={{
            opacity: Math.max(0, 1 - scrollProgress * 1.5),
            transform: `translate3d(${-mouseOffset.x * 0.6}px, ${-mouseOffset.y * 0.3 + scrollProgress * -30}px, 0)`
          }}
        >
          <div className="relative h-full flex items-center justify-center md:justify-end">
            <div 
              className="relative w-full h-full overflow-hidden"
              style={{
                maskImage: 'linear-gradient(to right, transparent 0%, black 25%, black 85%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 15%, black 80%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 25%, black 85%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 15%, black 80%, transparent 100%)',
                maskComposite: 'intersect',
                WebkitMaskComposite: 'destination-in',
              }}
            >
              <img
                src={COOKING_IMAGE}
                alt="Cooking in progress"
                data-testid="hero-cooking-image"
                className="w-full h-full object-cover"
              />
              {/* Steam overlay on cooking image */}
              <div className="absolute inset-0 overflow-hidden">
                <SteamAnimation className="bottom-1/4 left-0 right-0" delay={0} />
              </div>
            </div>
          </div>
        </div>

        {/* Transition text */}
        <div 
          className="absolute inset-0 z-25 flex items-end justify-center pb-32 pointer-events-none"
          style={{ 
            opacity: scrollProgress > 0.4 && scrollProgress < 0.8 
              ? Math.min(1, (scrollProgress - 0.4) / 0.2) * Math.max(0, 1 - (scrollProgress - 0.6) / 0.2)
              : 0 
          }}
        >
          <p className="font-cormorant italic text-3xl md:text-4xl lg:text-5xl text-white text-center">
            Every kitchen tells a story.
          </p>
        </div>

        {/* Cookware Collection Reveal — appears after transition text */}
        <div 
          className="absolute inset-0 z-[22] flex items-center justify-center pointer-events-none"
          style={{ 
            opacity: scrollProgress > 0.55 
              ? Math.min(1, (scrollProgress - 0.55) / 0.25) 
              : 0,
            transform: `scale(${0.9 + Math.min(0.1, (scrollProgress - 0.55) * 0.4)}) translate3d(0, ${Math.max(0, (0.7 - scrollProgress) * 60)}px, 0)`
          }}
        >
          <div 
            className="relative w-[90%] max-w-4xl"
            style={{
              maskImage: 'radial-gradient(ellipse 55% 55% at 50% 45%, black 20%, transparent 100%)',
              WebkitMaskImage: 'radial-gradient(ellipse 55% 55% at 50% 45%, black 20%, transparent 100%)',
            }}
          >
            <img
              src={COOKWARE_COLLECTION_URL}
              alt="Tex-Style Company Complete Cookware Collection"
              data-testid="hero-cookware-collection"
              className="w-full h-auto object-contain"
            />
          </div>
          {/* Subtle glow behind the collection */}
          <div 
            className="absolute inset-0 -z-10 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse at 50% 50%, rgba(107, 68, 204, 0.08) 0%, transparent 50%)',
            }}
          />
        </div>

        {/* Eyebrow */}
        <div 
          className="absolute top-24 left-0 right-0 z-40 text-left px-6 md:px-12 lg:px-24"
          style={{ 
            opacity: textOpacity,
            transform: `translate3d(${mouseOffset.x * 0.1}px, ${mouseOffset.y * 0.1}px, 0)`
          }}
        >
          <p
            data-testid="hero-eyebrow"
            className="font-raleway text-[10px] md:text-[11px] font-semibold uppercase tracking-[0.5em] text-tex-violet-bright"
          >
            Premium Kitchenware
          </p>
        </div>

        {/* Bottom content */}
        <div 
          className="absolute bottom-12 left-0 z-40 px-6 md:px-12 lg:px-24"
          style={{ opacity: Math.max(0, 1 - scrollProgress * 3) }}
        >
          <p
            data-testid="hero-subline"
            className="font-raleway text-sm md:text-base font-light text-tex-muted max-w-md leading-relaxed mb-8"
          >
            Thoughtfully engineered kitchenware,
            <br />
            for those who take their kitchen seriously.
          </p>
          
          <div className="mb-8">
            <MagneticButton href="https://shop.texstyleco.com" scrollProgress={scrollProgress}>
              Explore the Collection
            </MagneticButton>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="font-raleway text-[10px] uppercase tracking-[0.3em] text-tex-muted/60">
              Scroll to explore
            </span>
            <div className="w-12 h-[1px] bg-gradient-to-r from-tex-violet to-transparent" />
          </div>
        </div>

        {/* Vignette */}
        <div 
          className="absolute inset-0 z-[4] pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 100% 100% at 50% 50%, transparent 30%, rgba(0,0,0,0.5) 100%)'
          }}
        />
      </div>
    </section>
  );
};

export default HeroSection;
